import { useSessionKey } from "@auth/sessionStore";
import { faPlus, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HoldSettingsCard } from "@phone/HoldSettingsCard";
import { MessageEditor } from "@phone/MessageEditor";
import { OutboundSettingsCards } from "@phone/OutboundSettingsCards";
import {
  formatPhoneNumber,
  MAX_HANDOFF_DESTINATIONS,
  PhoneSetupValidationError,
  validateDestinations,
  type PhoneLine,
  type PhoneMessageConfig,
  type PhoneMessagePurpose,
  type PhoneSetup,
} from "@phone/phoneSetupClient";
import {
  usePhoneSetup,
  useUpdateHandoffDestinations,
  useUpdateOutboundConfig,
} from "@phone/phoneSetupQueries";
import { OUTBOUND_DISCLOSURE_MAX_SECONDS } from "@phone/recordWav";
import {
  Button,
  ConfirmDialog,
  FieldError,
  IconButton,
  Input,
  SettingsCard,
  Swipe,
  Toggle,
} from "@scribenote/brian-design-system";
import { useRef, useState } from "react";

/** Until operations provisions the line there is nothing to configure, so it shows the checklist. */
export const PhoneSettingsPanel = () => {
  const sessionKey = useSessionKey();
  const setupQuery = usePhoneSetup(sessionKey);

  return (
    <div className="flex flex-col gap-6 font-body">
      {setupQuery.isLoading && (
        <p className="text-sm text-annotation">Checking…</p>
      )}

      {setupQuery.isError && (
        <FieldError data-testid="phone-setup-error">
          Couldn't reach the server.
        </FieldError>
      )}

      {setupQuery.data &&
        (setupQuery.data.line === null ? (
          <PhoneOnboardingCard />
        ) : (
          <ConfiguredPhonePanel
            setup={setupQuery.data}
            line={setupQuery.data.line}
          />
        ))}
    </div>
  );
};

const ConfiguredPhonePanel = ({
  setup,
  line,
}: {
  setup: PhoneSetup;
  line: PhoneLine;
}) => (
  <>
    <PhoneLineCard line={line} />
    <MessageCard
      purpose="recording_disclosure"
      testIdPrefix="phone-disclosure"
      title="Recording disclosure"
      subtitle="What callers hear first on every call, telling them the call is recorded."
      hears="Callers will hear"
      config={setup.messages.recordingDisclosure}
    />
    <MessageCard
      purpose="voicemail_greeting"
      testIdPrefix="phone-greeting"
      title="Voicemail greeting"
      subtitle="What callers hear when I pick up a call your clinic missed."
      hears="Missed callers will hear"
      config={setup.messages.voicemailGreeting}
    />
    <HoldSettingsCard hold={setup.hold} />
    <HandoffCard setup={setup} brianNumber={line.inboundDidE164} />
    <OutboundSettingsCards
      setup={setup}
      afterToggle={
        <OutboundDisclosureCard
          setup={setup}
          config={setup.messages.outboundRecordingDisclosure}
        />
      }
    />
  </>
);

const OutboundDisclosureCard = ({
  setup,
  config,
}: {
  setup: PhoneSetup;
  config: PhoneMessageConfig;
}) => {
  const update = useUpdateOutboundConfig();
  return (
    <MessageCard
      purpose="outbound_recording_disclosure"
      testIdPrefix="phone-outbound-disclosure"
      title="Outbound recording notice"
      subtitle="What the person you call hears once the call connects, telling them the call is recorded. Your team won't hear it. Separate from the inbound disclosure."
      hears="The person you call will hear"
      config={config}
      spoken={{
        on: setup.outboundDisclosureSpoken,
        pending: update.isPending,
        failed: update.isError,
        onChange: (on) =>
          update.mutate({
            disclosureSpoken: on,
          }),
      }}
    />
  );
};

/** View-only: changing a number is ops work, not a knob. */
const PhoneLineCard = ({ line }: { line: PhoneLine }) => (
  <SettingsCard
    title="Phone line"
    subtitle="The number I answer when your clinic misses a call. Contact Brian support to change it."
    data-testid="phone-line-card"
  >
    <dl className="flex flex-col gap-3 text-sm">
      <div className="flex flex-col gap-0.5">
        <dt className="text-annotation-caps text-annotation">Brian number</dt>
        <dd className="font-mono text-ink" data-testid="phone-line-number">
          {formatPhoneNumber(line.inboundDidE164)}
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-annotation-caps text-annotation">Status</dt>
        <dd>
          {line.enabled ? (
            <Swipe tone="working">Active</Swipe>
          ) : (
            <Swipe tone="stopped">Disabled</Swipe>
          )}
        </dd>
      </div>
    </dl>
  </SettingsCard>
);

const MessageCard = ({
  purpose,
  testIdPrefix,
  title,
  subtitle,
  hears,
  config,
  spoken,
}: {
  purpose: PhoneMessagePurpose;
  testIdPrefix: string;
  title: string;
  subtitle: string;
  hears: string;
  config: PhoneMessageConfig;
  /** Off: Brian plays nothing and staff give the notice themselves. */
  spoken?: {
    on: boolean;
    pending: boolean;
    failed: boolean;
    onChange: (on: boolean) => void;
  };
}) => (
  <SettingsCard
    title={title}
    subtitle={subtitle}
    data-testid={`${testIdPrefix}-card`}
  >
    <div className="flex flex-col gap-4">
      {spoken && (
        <Toggle
          checked={spoken.on}
          disabled={spoken.pending}
          onCheckedChange={spoken.onChange}
          label={
            spoken.on
              ? "Brian plays the notice when the call connects"
              : "Your team gives the notice themselves"
          }
          data-testid={`${testIdPrefix}-spoken`}
        />
      )}
      {spoken?.failed && (
        <FieldError data-testid={`${testIdPrefix}-spoken-error`}>
          Couldn't change the recording notice setting. Try again.
        </FieldError>
      )}
      {spoken && !spoken.on && (
        <p
          className="text-sm text-annotation"
          data-testid={`${testIdPrefix}-spoken-off`}
        >
          Brian plays nothing on these calls. Your team is responsible for
          telling the other party the call is recorded.
        </p>
      )}
      <MessageEditor
        purpose={purpose}
        testIdPrefix={testIdPrefix}
        controlLabel={`${title} mode`}
        hears={hears}
        config={config}
        maxRecordSeconds={
          purpose === "outbound_recording_disclosure"
            ? OUTBOUND_DISCLOSURE_MAX_SECONDS
            : undefined
        }
        controlsHidden={spoken !== undefined && !spoken.on}
      />
    </div>
  </SettingsCard>
);

/** The key survives removals above it. */
interface RowDraft {
  key: string;
  number: string;
}

const from422 = (
  error: unknown,
  rowCount: number,
): { rows: (string | undefined)[]; general: string | null } => {
  const rows: (string | undefined)[] = Array.from({ length: rowCount });
  if (!(error instanceof PhoneSetupValidationError))
    return { rows, general: null };
  let general: string | null = null;
  for (const fieldError of error.fieldErrors) {
    const index = fieldError.loc.find(
      (part): part is number => typeof part === "number",
    );
    if (index !== undefined && index < rowCount) rows[index] ??= fieldError.msg;
    else general ??= fieldError.msg;
  }
  return { rows, general };
};

/** No ordering: routing picks whichever line is free. Saved as a full replacement. */
const HandoffCard = ({
  setup,
  brianNumber,
}: {
  setup: PhoneSetup;
  brianNumber: string;
}) => {
  const updateDestinations = useUpdateHandoffDestinations();
  const [rows, setRows] = useState<RowDraft[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const nextKey = useRef(0);

  const draftRows =
    rows ??
    setup.handoffDestinations.map(
      (number, index): RowDraft => ({
        key: `server-${index}`,
        number,
      }),
    );

  const edit = (next: RowDraft[]) => {
    updateDestinations.reset();
    setRows(next);
  };

  const setRow = (key: string, number: string) =>
    edit(draftRows.map((r) => (r.key === key ? { ...r, number } : r)));

  const removeRow = (key: string) =>
    edit(draftRows.filter((r) => r.key !== key));

  const addRow = () =>
    edit([...draftRows, { key: `new-${nextKey.current++}`, number: "" }]);

  const numbers = draftRows.map((row) => row.number.trim());
  const validation = validateDestinations(numbers, brianNumber);
  const server = from422(updateDestinations.error, draftRows.length);

  const dirty =
    rows !== null &&
    (numbers.length !== setup.handoffDestinations.length ||
      numbers.some((number, i) => number !== setup.handoffDestinations[i]));

  const listError = validation.list ?? server.general;

  const save = () => {
    updateDestinations.mutate(numbers, {
      onSuccess: () => setRows(null),
      onSettled: () => setConfirming(false),
    });
  };

  return (
    <SettingsCard
      title="Handoff numbers"
      subtitle="The lines I can transfer a caller to when they need a person. I try these and forward the call to one that's free; if nobody is, I take a message."
      data-testid="phone-handoff-card"
    >
      <div className="flex flex-col gap-4">
        {draftRows.length === 0 && (
          <p
            className="text-sm text-annotation"
            data-testid="phone-handoff-empty"
          >
            No handoff numbers yet — I'll take a message instead of handing off.
          </p>
        )}

        {draftRows.map((row, index) => {
          const rowError = validation.rows[index] ?? server.rows[index];
          return (
            <div key={row.key} className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <Input
                  aria-label={`Handoff number ${index + 1}`}
                  value={row.number}
                  onChange={(e) => setRow(row.key, e.target.value)}
                  placeholder="+15551234567"
                  error={row.number.trim() === "" ? undefined : rowError}
                  data-testid={`phone-destination-number-${index}`}
                />
              </div>
              <IconButton
                aria-label={`Remove handoff number ${index + 1}`}
                size="sm"
                className="mt-1"
                onClick={() => removeRow(row.key)}
                data-testid={`phone-destination-remove-${index}`}
              >
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
            </div>
          );
        })}

        {listError && (
          <FieldError data-testid="phone-handoff-list-error">
            {listError}
          </FieldError>
        )}

        {updateDestinations.isError &&
          !(updateDestinations.error instanceof PhoneSetupValidationError) && (
            <FieldError data-testid="phone-handoff-save-error">
              Couldn't save the handoff numbers. Try again.
            </FieldError>
          )}

        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={draftRows.length >= MAX_HANDOFF_DESTINATIONS}
            onClick={addRow}
            data-testid="phone-destination-add"
          >
            <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
            Add number
          </Button>
          <Button
            size="sm"
            disabled={!dirty || !validation.valid}
            onClick={() => setConfirming(true)}
            data-testid="phone-handoff-save"
          >
            Save
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        data-testid={confirming ? "phone-handoff-confirm" : undefined}
        title="Replace the handoff numbers?"
        confirmLabel="Save numbers"
        cancelLabel="Cancel"
        busy={updateDestinations.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={save}
        confirmTestId="phone-handoff-confirm-confirm"
        cancelTestId="phone-handoff-confirm-cancel"
      >
        <p className="text-sm text-ink-body">
          {numbers.length === 0
            ? "With no handoff numbers, I'll take a message instead of handing anyone off. Continue?"
            : "Callers who need a person will be forwarded to whichever of these lines is free:"}
        </p>
        {numbers.length > 0 && (
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 font-mono text-sm text-ink">
            {numbers.map((number, i) => (
              <li key={i}>{formatPhoneNumber(number)}</li>
            ))}
          </ul>
        )}
      </ConfirmDialog>
    </SettingsCard>
  );
};

const ONBOARDING_STEPS: { title: string; body: string }[] = [
  {
    title: "Brian gets assigned a number for your clinic",
    body: "We provision a dedicated phone number for your clinic. Contact Brian support to start — this step is on us.",
  },
  {
    title: "Forward missed calls into Brian",
    body: "Your clinic's phone system forwards unanswered calls to the Brian number, so I only ever pick up what your team couldn't. We'll walk through it with your phone provider.",
  },
  {
    title: "Choose handoff numbers",
    body: "Pick up to five clinic lines I can transfer a caller to when they need a person — I forward the call to one that's free. You'll set these right here once the line is live.",
  },
  {
    title: "Make a test call",
    body: "Call your clinic, let it ring through, and hear me answer. Then the line is yours.",
  },
];

const PhoneOnboardingCard = () => (
  <SettingsCard
    title="Set up your phone line"
    subtitle="Your clinic doesn't have a Brian phone number yet. Here's how it gets one."
    data-testid="phone-onboarding-card"
  >
    <ol className="flex flex-col gap-4">
      {ONBOARDING_STEPS.map((step, index) => (
        <li key={step.title} className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="flex size-7 shrink-0 items-center justify-center rounded-full bg-recess font-mono text-sm text-annotation"
          >
            {index + 1}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink">{step.title}</p>
            <p className="mt-0.5 text-sm text-annotation">{step.body}</p>
          </div>
        </li>
      ))}
    </ol>
    <p className="mt-4 text-sm text-annotation">
      Ready to start, or stuck on a step? Contact Brian support and we'll get
      your line set up.
    </p>
  </SettingsCard>
);
